import { PillVariant } from '@/components/shared/components/Pill';
import {
    emDash,
    formatDateWithTimezone,
    getDateFromUtcString,
} from '@/components/shared/utils/utils';

import {
    VendorStatus,
    VendorStatusGroup,
    VendorStatusResponse,
    VendorStatusRow,
} from './VendorStatusTable.types';

const STATUS_VARIANTS: Record<VendorStatus, PillVariant> = {
    [VendorStatus.Unknown]: 'grey',
    [VendorStatus.NotApplicable]: 'grey',
    [VendorStatus.NotRequested]: 'grey',
    [VendorStatus.Pending]: 'amber',
    [VendorStatus.Completed]: 'green',
    [VendorStatus.Errored]: 'red',
    [VendorStatus.Rejected]: 'red',
};

// The enum values are the wire contract, so the pill needs its own spaced copy of the two joined names.
const STATUS_LABELS: Record<VendorStatus, string> = {
    [VendorStatus.Unknown]: 'Unknown',
    [VendorStatus.NotApplicable]: 'Not Applicable',
    [VendorStatus.NotRequested]: 'Not Requested',
    [VendorStatus.Pending]: 'Pending',
    [VendorStatus.Completed]: 'Completed',
    [VendorStatus.Errored]: 'Errored',
    [VendorStatus.Rejected]: 'Rejected',
};

const STATUS_SENTENCES: Record<VendorStatus, string> = {
    [VendorStatus.Unknown]: 'Unknown.',
    [VendorStatus.NotApplicable]: 'Not applicable.',
    [VendorStatus.NotRequested]: 'Not requested yet.',
    [VendorStatus.Pending]: 'Requested. Pending Response.',
    [VendorStatus.Completed]: 'Completed.',
    [VendorStatus.Errored]: 'Errored. No usable response.',
    [VendorStatus.Rejected]: 'Rejected by the vendor.',
};

// The portal can run against a newer API that sends a status this build has never heard of.
const toKnownStatus = (status: VendorStatus): VendorStatus =>
    Object.hasOwn(STATUS_LABELS, status) ? status : VendorStatus.Unknown;

export const getStatusVariant = (status: VendorStatus): PillVariant =>
    STATUS_VARIANTS[toKnownStatus(status)];

export const getStatusLabel = (status: VendorStatus): string =>
    STATUS_LABELS[toKnownStatus(status)];

// The API explains why a row never ran (e.g. Au10tix on a Highway application), or why the vendor
// rejected it (e.g. an Au10tix file that was too small), through reason.
const REASONED_STATUSES = new Set([
    VendorStatus.NotApplicable,
    VendorStatus.NotRequested,
    VendorStatus.Rejected,
]);

export const getStatusSentence = (row: VendorStatusRow): string =>
    REASONED_STATUSES.has(row.status) && row.reason
        ? row.reason
        : STATUS_SENTENCES[toKnownStatus(row.status)];

export const formatDuration = (ms: number): string => {
    // Clock skew between the API host and the reviewer's browser can make a fresh row look negative.
    const safeMs = Math.max(ms, 0);

    if (safeMs < 60_000) {
        return `${(safeMs / 1000).toFixed(1)}s`;
    }

    if (safeMs < 3_600_000) {
        const minutes = Math.floor(safeMs / 60_000);
        const seconds = Math.floor((safeMs % 60_000) / 1000);
        return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
    }

    const hours = Math.floor(safeMs / 3_600_000);
    const minutes = Math.floor((safeMs % 3_600_000) / 60_000);
    return `${hours}h ${minutes}m`;
};

// Zero-padded so the timestamps line up column-wise down the Timing cells.
const ARRIVAL_FORMAT: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
};

export const formatArrival = (iso: string): string =>
    formatDateWithTimezone(iso, ARRIVAL_FORMAT);

export const getTimingCell = (row: VendorStatusRow, now: number): string => {
    if (!row.requestedAt) {
        return emDash;
    }

    const requestedMs = getDateFromUtcString(row.requestedAt).getTime();

    if (isNaN(requestedMs)) {
        return emDash;
    }

    if (row.status === VendorStatus.Pending) {
        const waiting = formatDuration(now - requestedMs);

        return `Waiting ${waiting} · Submitted ${formatArrival(row.requestedAt)}`;
    }

    if (!row.completedAt) {
        return emDash;
    }

    const completedMs = getDateFromUtcString(row.completedAt).getTime();

    if (isNaN(completedMs)) {
        return emDash;
    }

    const took = formatDuration(completedMs - requestedMs);

    return `Took ${took} · Completed ${formatArrival(row.completedAt)}`;
};

// A group carries either rows or per-owner subgroups, and a pending vendor in either shape has to keep
// the waiting clock ticking.
const getGroupRows = (group: VendorStatusGroup): VendorStatusRow[] => [
    ...group.rows,
    ...(group.subgroups ?? []).flatMap((subgroup) => subgroup.rows),
];

// A row still pending this long after its request has stalled at the vendor, so polling would never end.
export const PENDING_POLL_WINDOW_MS = 60 * 60 * 1000;

export const hasRecentPendingRow = (
    response: VendorStatusResponse | undefined,
    now: number,
): boolean =>
    (response?.groups ?? []).some((group) =>
        getGroupRows(group).some(
            (row) =>
                row.status === VendorStatus.Pending &&
                row.requestedAt !== null &&
                now - getDateFromUtcString(row.requestedAt).getTime() < PENDING_POLL_WINDOW_MS,
        ),
    );
